import { computed, type ComputedRef, type Reactive, type Ref, type ShallowRef } from 'vue';
import type { RouteSearchRouteEntry } from 'src/models/apis/movie/movieRequest';
import type { GIMovieList, GIResource, MapSelectOpts } from 'src/models/geoItem';
import type { IExtremeMapRef, Location, MapElemInfo, MovieLayerMeta, MovieSearchMode, QueryArea } from 'src/models';
import type { TopMovieSearch } from 'src/models/top';
import { MOVIE_SEARCH_RESULT_MODES } from 'src/consts/top';

export interface MovieSearchExecutionContext {
  isMovieSearchResultModeNone: ComputedRef<boolean>;
  isMovieSearchResultModeDetail: ComputedRef<boolean>;
  isMovieSearchResultModeArea: ComputedRef<boolean>;
  isMovieSearchResultModeRoute: ComputedRef<boolean>;
  doSearchParamMovieSearch: () => Promise<void>;
  doRouteMovieSearch: (routes: { data: RouteSearchRouteEntry[] }) => Promise<void>;
  doAreaMovieSearch: (queryArea: QueryArea) => Promise<void>;
  setMovieQueryStart: (arg: { mode: MovieSearchMode }) => void;
  setMovieQueryDone: (arg: { results: GIMovieList[] }) => void;
  refreshMovieLayer: () => void;
}

// 検索実行（検索条件/ルート/範囲の各検索）と検索モード管理を担うcomposable。
// バリデーション結果はparams側の責務のため、computed参照を注入してもらう
// （params → executionの一方向依存。executionからparamsへは依存しない）。
export function useMovieSearchExecution(deps: {
  movieSearchState: Reactive<TopMovieSearch>;
  movieLayerMeta: ShallowRef<MovieLayerMeta>;
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  setMapSelectedElemInfo: (val: MapElemInfo<GIResource | Location> | null, opts?: MapSelectOpts) => void;
  getSelectedElemDataName: () => string | null;
  isValidMovieSearchParams: ComputedRef<boolean>;
  isValidRouteMovieSearchDateParams: ComputedRef<boolean>;
  isValidMovieSearchDateParams: ComputedRef<boolean>;
}): MovieSearchExecutionContext {
  const {
    movieSearchState,
    movieLayerMeta,
    refExtremeMap,
    setMapSelectedElemInfo,
    getSelectedElemDataName,
    isValidMovieSearchParams,
    isValidRouteMovieSearchDateParams,
    isValidMovieSearchDateParams,
  } = deps;

  const isMovieSearchResultModeNone = computed(() => {
    return movieSearchState.resultMode === MOVIE_SEARCH_RESULT_MODES.NONE;
  });
  const isMovieSearchResultModeDetail = computed(() => {
    return movieSearchState.resultMode === MOVIE_SEARCH_RESULT_MODES.DETAIL;
  });
  const isMovieSearchResultModeArea = computed(() => {
    return movieSearchState.resultMode === MOVIE_SEARCH_RESULT_MODES.AREA;
  });
  const isMovieSearchResultModeRoute = computed(() => {
    return movieSearchState.resultMode === MOVIE_SEARCH_RESULT_MODES.ROUTE;
  });

  const refreshMovieLayer = () => {
    if (refExtremeMap.value) {
      refExtremeMap.value.refreshMovieLayer(movieSearchState.results);
    }
  };

  const setMovieQueryStart = ({ mode }: { mode: MovieSearchMode }) => {
    movieSearchState.isQuerying = true;
    movieSearchState.results = [];
    movieSearchState.resultMode = mode;
    refreshMovieLayer();
    if (getSelectedElemDataName() === 'movie') {
      setMapSelectedElemInfo(null);
    }
    if (!isMovieSearchResultModeArea.value && refExtremeMap.value) {
      refExtremeMap.value.hideQueryArea();
    }
  };
  const setMovieQueryDone = ({ results }: { results: GIMovieList[] }) => {
    movieSearchState.isQuerying = false;
    movieSearchState.results = results;
    refreshMovieLayer();
  };
  // 検索開始/成功/失敗の状態管理をまとめ、各検索関数はfetchResultsのみを渡す
  const runMovieSearchQuery = async(mode: MovieSearchMode, fetchResults: () => Promise<GIMovieList[]>) => {
    setMovieQueryStart({ mode });
    try {
      const results = await fetchResults();
      setMovieQueryDone({ results });
    } catch (e) {
      movieSearchState.isQuerying = false;
    }
  };
  /* 検索条件指定による動画検索 */
  const doSearchParamMovieSearch = async() => {
    if (!isValidMovieSearchParams.value) return;

    const manager = movieLayerMeta.value.manager;
    if (!manager) return;

    await runMovieSearchQuery(
      MOVIE_SEARCH_RESULT_MODES.DETAIL,
      () => manager.getResourcesByParams(movieSearchState.params),
    );
  };
  /* ルート指定による動画検索 */
  const doRouteMovieSearch = async(routes: { data: RouteSearchRouteEntry[] }) => {
    if (!isValidRouteMovieSearchDateParams.value) return;

    const manager = movieLayerMeta.value.manager;
    if (!manager) return;

    await runMovieSearchQuery(
      MOVIE_SEARCH_RESULT_MODES.ROUTE,
      () => manager.getResourcesByRoutes(movieSearchState.params, routes),
    );
  };
  /* 範囲指定による動画検索 */
  const doAreaMovieSearch = async(queryArea: QueryArea) => {
    if (!refExtremeMap.value) return;

    if (!isValidMovieSearchDateParams.value) return;

    const manager = movieLayerMeta.value.manager;
    if (!manager) return;

    movieSearchState.areaQueryObj = queryArea;
    refExtremeMap.value.showQueryArea(queryArea);

    await runMovieSearchQuery(MOVIE_SEARCH_RESULT_MODES.AREA, async() => {
      const results = await manager.getResourcesByArea(movieSearchState.params, queryArea);
      // 付箋の範囲検索などのように、所望のソートを行いたい場合がある
      if (queryArea.resultSortFunc) {
        results.sort(queryArea.resultSortFunc);
      }
      return results;
    });
  };

  return {
    isMovieSearchResultModeNone,
    isMovieSearchResultModeDetail,
    isMovieSearchResultModeArea,
    isMovieSearchResultModeRoute,
    doSearchParamMovieSearch,
    doRouteMovieSearch,
    doAreaMovieSearch,
    setMovieQueryStart,
    setMovieQueryDone,
    refreshMovieLayer,
  };
}
